import { createRouter, createWebHistory } from 'vue-router';
import authService from '@/helpers/authservice/authService.js';

// 路由配置
const routes = [
    // 系統登入
    {
        path: '/system/login',
        name: 'SystemLogin',
        component: () => import('@/views/auth/system/Login.vue'),
        meta: { requiresAuth: false },
    },
    // 平台登入
    {
        path: '/platform/login',
        name: 'PlatformLogin',
        component: () => import('@/views/auth/platform/Login.vue'),
        meta: { requiresAuth: false },
    },
    // 客戶登入
    {
        path: '/client/login',
        name: 'ClientLogin',
        component: () => import('@/views/auth/client/Login.vue'),
        meta: { requiresAuth: false },
    },
    // 官網模組（system 帳號）
    {
        path: '/website',
        component: () => import('@/layouts/MainLayout.vue'),
        meta: { requiresAuth: true, accountType: 'system' },
        children: [
            {
                path: 'seo',
                name: 'WebsiteSeo',
                component: () => import('@/views/website/seo/Index.vue'),
                meta: { title: '官網 SEO 設定', accountType: ['system'] },
            },
            {
                // pageKey 可能含斜線（如 features/members）
                path: 'seo/:pageKey(.*)/edit',
                name: 'WebsiteSeoEdit',
                component: () => import('@/views/website/seo/Form.vue'),
                meta: { title: '編輯官網 SEO', accountType: ['system'] },
            },
            {
                path: 'blog',
                name: 'WebsiteBlogIndex',
                component: () => import('@/views/website/blog/Index.vue'),
                meta: { title: '最新消息', accountType: ['system'] },
            },
            {
                path: 'blog/create',
                name: 'WebsiteBlogCreate',
                component: () => import('@/views/website/blog/Form.vue'),
                meta: { title: '新增最新消息', accountType: ['system'] },
            },
            {
                path: 'blog/:id/edit',
                name: 'WebsiteBlogEdit',
                component: () => import('@/views/website/blog/Form.vue'),
                meta: { title: '編輯最新消息', accountType: ['system'] },
            },
            {
                path: 'contact-inquiries',
                name: 'WebsiteContactInquiries',
                component: () => import('@/views/website/contact-inquiries/Index.vue'),
                meta: { title: '聯絡詢問紀錄', accountType: ['system'] },
            },
        ],
    },
    // 系統 Dashboard
    {
        path: '/system',
        component: () => import('@/layouts/MainLayout.vue'),
        meta: { requiresAuth: true, accountType: 'system' },
        children: [
            {
                path: 'dashboard',
                name: 'SystemDashboard',
                component: () => import('@/views/dashboard/SystemDashboard.vue'),
                meta: { title: '系統管理 Dashboard' },
            },
            {
                path: 'accounts',
                name: 'SystemAccounts',
                component: () => import('@/views/system/accounts/Index.vue'),
                meta: { title: '系統帳號列表' },
            },
            {
                path: 'accounts/create',
                name: 'CreateSystemAccount',
                component: () => import('@/views/system/accounts/Create.vue'),
                meta: { title: '新增系統帳號' },
            },
            {
                path: 'accounts/:id',
                name: 'SystemAccountDetail',
                component: () => import('@/views/system/accounts/Detail.vue'),
                meta: { title: '系統帳號詳情' },
            },
            {
                path: 'accounts/:id/edit',
                name: 'EditSystemAccount',
                component: () => import('@/views/system/accounts/Create.vue'),
                meta: { title: '編輯系統帳號' },
            },
            {
                path: 'platforms/create',
                name: 'CreatePlatform',
                component: () => import('@/views/system/platforms/management/Create.vue'),
                meta: { title: '新增平台' },
            },
            {
                path: 'platforms/:id/edit',
                name: 'EditPlatform',
                component: () => import('@/views/system/platforms/management/Create.vue'),
                meta: { title: '編輯平台' },
            },
            {
                path: 'platforms',
                name: 'PlatformManagement',
                component: () => import('@/views/system/platforms/management/Index.vue'),
                meta: { title: '平台列表' },
            },
            {
                path: 'platforms/accounts/create',
                name: 'CreatePlatformAccount',
                component: () => import('@/views/system/platforms/accounts/Create.vue'),
                meta: { title: '新增平台帳號' },
            },
            {
                path: 'platforms/accounts/:id/edit',
                name: 'EditPlatformAccount',
                component: () => import('@/views/system/platforms/accounts/Create.vue'),
                meta: { title: '編輯平台帳號' },
            },
            {
                path: 'platforms/accounts',
                name: 'PlatformAccounts',
                component: () => import('@/views/system/platforms/accounts/Index.vue'),
                meta: { title: '平台帳號管理' },
            },
            {
                path: 'clients',
                name: 'ClientList',
                component: () => import('@/views/system/clients/Index.vue'),
                meta: { title: '客戶資料列表', accountType: ['system', 'platform'] },
            },
            {
                path: 'clients/create',
                name: 'CreateClient',
                component: () => import('@/views/system/clients/Create.vue'),
                meta: { title: '新增客戶', accountType: ['system', 'platform'] },
            },
            {
                path: 'clients/:id/edit',
                name: 'EditClient',
                component: () => import('@/views/system/clients/Create.vue'),
                meta: { title: '編輯客戶' },
            },
            {
                path: 'clients/:id/optional-modules',
                name: 'ClientOptionalModules',
                component: () => import('@/views/system/clients/OptionalModules.vue'),
                meta: { title: '選購模組管理' },
            },
            {
                path: 'clients/accounts',
                name: 'SystemClientAccounts',
                component: () => import('@/views/platform/clients/accounts/Index.vue'),
                meta: { title: '客戶帳號管理', accountType: ['system', 'platform'] },
            },
            {
                path: 'clients/accounts/create',
                name: 'SystemCreateClientAccount',
                component: () => import('@/views/platform/clients/accounts/Create.vue'),
                meta: { title: '新增客戶帳號', accountType: ['system', 'platform'] },
            },
            {
                path: 'clients/accounts/:id/edit',
                name: 'SystemEditClientAccount',
                component: () => import('@/views/platform/clients/accounts/Create.vue'),
                meta: { title: '編輯客戶帳號', accountType: ['system', 'platform'] },
            },
            {
                path: 'settings',
                name: 'SystemSettings',
                component: () => import('@/views/system/settings/Index.vue'),
                meta: { title: '系統設定' },
            },
            {
                path: 'commerce/storefront-templates',
                name: 'SystemCommerceStorefrontTemplates',
                component: () => import('@/views/system/commerce/StorefrontTemplateSettings.vue'),
                meta: { title: '商城前台模板設定', accountType: 'system' },
            },
            // 舊聯絡詢問路徑硬切導向官網模組
            {
                path: 'contact-inquiries',
                redirect: '/website/contact-inquiries',
            },
            {
                path: 'contact-inquiries/:id',
                redirect: '/website/contact-inquiries',
            },
            {
                path: 'quote-estimator',
                name: 'SystemQuoteEstimator',
                component: () => import('@/views/system/quote-estimator/Index.vue'),
                meta: { title: '報價管理', accountType: ['system'] },
            },
            {
                path: 'plans',
                name: 'PlanList',
                component: () => import('@/views/system/plans/Index.vue'),
                meta: { title: '方案列表' },
            },
            {
                path: 'plans/create',
                name: 'CreatePlan',
                component: () => import('@/views/system/plans/Create.vue'),
                meta: { title: '新增方案' },
            },
            {
                path: 'plans/:id/edit',
                name: 'EditPlan',
                component: () => import('@/views/system/plans/Create.vue'),
                meta: { title: '編輯方案' },
            },
            {
                path: 'modules',
                name: 'ModuleList',
                component: () => import('@/views/system/modules/Index.vue'),
                meta: { title: '模組列表' },
            },
            {
                path: 'modules/create',
                name: 'CreateModule',
                component: () => import('@/views/system/modules/Create.vue'),
                meta: { title: '新增模組' },
            },
            {
                path: 'modules/:id/edit',
                name: 'EditModule',
                component: () => import('@/views/system/modules/Create.vue'),
                meta: { title: '編輯模組' },
            },
            {
                path: 'programs',
                name: 'ProgramList',
                component: () => import('@/views/system/programs/Index.vue'),
                meta: { title: '程式列表' },
            },
            // 開發工具 - 圖示展示
            {
                path: 'dev/icons',
                name: 'DevIconShowcase',
                component: () => import('@/views/dev/IconShowcase.vue'),
                meta: { title: '圖示展示與測試' },
            },
            {
                path: 'programs/create',
                name: 'CreateProgram',
                component: () => import('@/views/system/programs/Create.vue'),
                meta: { title: '新增程式' },
            },
            {
                path: 'programs/:id/edit',
                name: 'EditProgram',
                component: () => import('@/views/system/programs/Create.vue'),
                meta: { title: '編輯程式' },
            },
            {
                path: 'subscription-clients',
                name: 'SubscriptionClientsList',
                component: () => import('@/views/system/subscription-clients/Index.vue'),
                meta: { title: '訂閱客戶管理', accountType: ['system', 'platform'] },
            },
            {
                path: 'roles',
                name: 'Roles',
                component: () => import('@/views/system/roles/Index.vue'),
                meta: { title: '角色管理' },
            },
            {
                path: 'roles/create',
                name: 'CreateRole',
                component: () => import('@/views/system/roles/Create.vue'),
                meta: { title: '新增角色' },
            },
            {
                path: 'roles/:roleCode/edit',
                name: 'EditRole',
                component: () => import('@/views/system/roles/Create.vue'),
                meta: { title: '編輯角色' },
            },
            {
                path: 'roles/:roleCode/modules',
                name: 'RoleModules',
                component: () => import('@/views/system/roles/Modules.vue'),
                meta: { title: '設定角色模組' },
            },
            {
                path: 'manual-payments/due-soon',
                name: 'ManualPaymentDueSoon',
                component: () => import('@/views/system/manual-payments/DueSoon.vue'),
                meta: { title: '即將到期款項提醒', accountType: ['system', 'platform'] },
            },
            {
                path: 'manual-payments/grace-period',
                name: 'ManualPaymentGracePeriod',
                component: () => import('@/views/system/manual-payments/GracePeriod.vue'),
                meta: { title: '寬限期款項提醒', accountType: ['system', 'platform'] },
            },
            {
                path: 'manual-payments/overdue',
                name: 'ManualPaymentOverdue',
                component: () => import('@/views/system/manual-payments/Overdue.vue'),
                meta: { title: '超過寬限期未收款列表', accountType: ['system', 'platform'] },
            },
            {
                path: 'billing',
                name: 'BillingManagement',
                component: () => import('@/views/system/billing/Index.vue'),
                meta: { title: '財務管理', accountType: ['system', 'platform'] },
            },
            {
                path: 'billing/overview',
                name: 'BillingOverview',
                component: () => import('@/views/system/billing/Overview.vue'),
                meta: { title: '帳單總覽', accountType: ['system', 'platform'] },
            },
            {
                path: 'billing/payment-records',
                name: 'PaymentRecords',
                component: () => import('@/views/system/billing/PaymentRecords.vue'),
                meta: { title: '付款記錄', accountType: ['system', 'platform'] },
            },
            {
                path: 'billing/reports',
                name: 'FinancialReports',
                component: () => import('@/views/system/billing/Reports.vue'),
                meta: { title: '財務報表', accountType: ['system', 'platform'] },
            },
            // 系統路由下的 403 頁面（使用 MainLayout）
            {
                path: 'forbidden',
                name: 'SystemForbidden',
                component: () => import('@/views/errors/403.vue'),
                meta: { title: '沒有權限' },
            },
            // 系統路由下的 404 頁面（使用 MainLayout）
            {
                path: ':pathMatch(.*)*',
                name: 'SystemNotFound',
                component: () => import('@/views/errors/404.vue'),
                meta: { title: '頁面不存在' },
            },
        ],
    },
    // 平台 Dashboard
    {
        path: '/platform',
        component: () => import('@/layouts/MainLayout.vue'),
        meta: { requiresAuth: true, accountType: 'platform' },
        children: [
            {
                path: 'dashboard',
                name: 'PlatformDashboard',
                component: () => import('@/views/dashboard/PlatformDashboard.vue'),
            },
            {
                path: 'clients/accounts',
                name: 'ClientAccounts',
                component: () => import('@/views/platform/clients/accounts/Index.vue'),
                meta: { title: '客戶帳號管理' },
            },
            {
                path: 'clients/accounts/create',
                name: 'CreateClientAccount',
                component: () => import('@/views/platform/clients/accounts/Create.vue'),
                meta: { title: '新增客戶帳號' },
            },
            {
                path: 'clients/accounts/:id/edit',
                name: 'EditClientAccount',
                component: () => import('@/views/platform/clients/accounts/Create.vue'),
                meta: { title: '編輯客戶帳號' },
            },
            // 平台路由下的 403 頁面（使用 MainLayout）
            {
                path: 'forbidden',
                name: 'PlatformForbidden',
                component: () => import('@/views/errors/403.vue'),
                meta: { title: '沒有權限' },
            },
            // 平台路由下的 404 頁面（使用 MainLayout）
            {
                path: ':pathMatch(.*)*',
                name: 'PlatformNotFound',
                component: () => import('@/views/errors/404.vue'),
                meta: { title: '頁面不存在' },
            },
        ],
    },
    // 客戶 Dashboard
    {
        path: '/client',
        component: () => import('@/layouts/MainLayout.vue'),
        meta: { requiresAuth: true, accountType: ['system', 'platform', 'client'] },
        children: [
            {
                path: 'dashboard',
                name: 'ClientDashboard',
                component: () => import('@/views/dashboard/ClientDashboard.vue'),
                meta: {
                    title: '營運總覽',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'staff/accounts',
                name: 'ClientStaffAccounts',
                component: () => import('@/views/client/staff-accounts/Index.vue'),
                meta: {
                    title: '員工帳號管理',
                    accountType: ['client']
                },
            },
            {
                path: 'staff/accounts/create',
                name: 'ClientStaffAccountCreate',
                component: () => import('@/views/client/staff-accounts/Create.vue'),
                meta: {
                    title: '新增員工帳號',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'staff/accounts/:id/edit',
                name: 'ClientStaffAccountEdit',
                component: () => import('@/views/client/staff-accounts/Create.vue'),
                meta: {
                    title: '編輯員工帳號',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'members',
                name: 'ClientMembers',
                component: () => import('@/views/client/members/Index.vue'),
                meta: {
                    title: '會員管理',
                    accountType: ['client']
                },
            },
            {
                path: 'members/import-history',
                name: 'ClientMemberImportHistory',
                component: () => import('@/views/client/members/ImportHistory.vue'),
                meta: {
                    title: '會員匯入 / 匯出紀錄',
                    accountType: ['client']
                },
            },
            {
                path: 'members/create',
                name: 'ClientMemberCreate',
                component: () => import('@/views/client/members/Create.vue'),
                meta: {
                    title: '新增會員',
                    accountType: ['client']
                },
            },
            {
                path: 'members/:id',
                name: 'ClientMemberDetail',
                component: () => import('@/views/client/members/Detail.vue'),
                meta: {
                    title: '會員詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'members/:id/edit',
                name: 'ClientMemberEdit',
                component: () => import('@/views/client/members/Create.vue'),
                meta: {
                    title: '編輯會員',
                    accountType: ['client']
                },
            },
            // 隱私權政策管理
            {
                path: 'policies',
                name: 'ClientPolicyList',
                component: () => import('@/views/client/policies/Index.vue'),
                meta: {
                    title: '隱私權政策',
                    accountType: ['client']
                },
            },
            {
                path: 'policies/create',
                name: 'ClientCreatePolicy',
                component: () => import('@/views/client/policies/Create.vue'),
                meta: {
                    title: '新增隱私權政策',
                    accountType: ['client']
                },
            },
            {
                path: 'policies/:id/edit',
                name: 'ClientEditPolicy',
                component: () => import('@/views/client/policies/Create.vue'),
                meta: {
                    title: '編輯隱私權政策',
                    accountType: ['client']
                },
            },
            // 服務條款管理
            {
                path: 'terms',
                name: 'ClientTermsList',
                component: () => import('@/views/client/terms/Index.vue'),
                meta: {
                    title: '服務條款',
                    accountType: ['client']
                },
            },
            {
                path: 'terms/create',
                name: 'ClientCreateTerms',
                component: () => import('@/views/client/terms/Create.vue'),
                meta: {
                    title: '新增服務條款',
                    accountType: ['client']
                },
            },
            {
                path: 'terms/:id/edit',
                name: 'ClientEditTerms',
                component: () => import('@/views/client/terms/Create.vue'),
                meta: {
                    title: '編輯服務條款',
                    accountType: ['client']
                },
            },
            {
                path: 'orders',
                name: 'ClientOrders',
                component: () => import('@/views/client/orders/Index.vue'),
                meta: {
                    title: '訂單列表',
                    accountType: ['client']
                },
            },
            {
                path: 'orders/create',
                name: 'ClientOrderCreate',
                component: () => import('@/views/client/orders/Create.vue'),
                meta: {
                    title: '新增訂單',
                    accountType: ['client']
                },
            },
            {
                path: 'orders/import',
                name: 'ClientOrderImport',
                component: () => import('@/views/client/orders/Import.vue'),
                meta: {
                    title: '批次匯入訂單',
                    accountType: ['client']
                },
            },
            {
                path: 'orders/import/history',
                name: 'ClientOrderImportHistory',
                component: () => import('@/views/client/orders/ImportHistory.vue'),
                meta: {
                    title: '匯入紀錄',
                    accountType: ['client']
                },
            },
            {
                path: 'orders/ecom/config',
                name: 'ClientEcomConfig',
                component: () => import('@/views/client/orders/EcomConfig.vue'),
                meta: {
                    title: '電商平台設定',
                    accountType: ['client']
                },
            },
            {
                path: 'orders/ecom/config/new/:platform',
                name: 'ClientEcomConfigCreate',
                component: () => import('@/views/client/orders/EcomConfigEdit.vue'),
                meta: {
                    title: '新增電商平台設定',
                    accountType: ['client']
                },
            },
            {
                path: 'orders/ecom/config/:configId',
                name: 'ClientEcomConfigEdit',
                component: () => import('@/views/client/orders/EcomConfigEdit.vue'),
                meta: {
                    title: '編輯電商平台設定',
                    accountType: ['client']
                },
            },
            {
                path: 'products',
                name: 'ClientProducts',
                component: () => import('@/views/client/products/Index.vue'),
                meta: {
                    title: '商品管理',
                    accountType: ['client']
                },
            },
            {
                path: 'products/create',
                name: 'ClientProductCreate',
                component: () => import('@/views/client/products/Form.vue'),
                meta: {
                    title: '新增商品',
                    accountType: ['client']
                },
            },
            {
                path: 'products/:id/edit',
                name: 'ClientProductEdit',
                component: () => import('@/views/client/products/Form.vue'),
                meta: {
                    title: '編輯商品',
                    accountType: ['client']
                },
            },
            {
                path: 'products/attributes',
                name: 'ClientProductAttributes',
                component: () => import('@/views/client/products/AttributesIndex.vue'),
                meta: {
                    title: '商品屬性定義',
                    accountType: ['client']
                },
            },
            {
                path: 'products/categories',
                name: 'ClientProductCategories',
                component: () => import('@/views/client/products/categories/Index.vue'),
                meta: {
                    title: '商品分類管理',
                    accountType: ['client']
                },
            },
            // 進銷存設定
            {
                path: 'inventory-settings',
                name: 'ClientInventorySettings',
                component: () => import('@/views/client/inventory/Settings.vue'),
                meta: {
                    title: '進銷存設定',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            // 系統參數（含 Wixtar 整合分頁；staff 可進入以管理 Wixtar）
            {
                path: 'settings',
                name: 'ClientSystemSettings',
                component: () => import('@/views/client/settings/Index.vue'),
                meta: {
                    title: '系統參數',
                    accountType: ['client']
                },
            },
            {
                path: 'commerce/settings',
                name: 'ClientCommerceSettings',
                component: () => import('@/views/client/commerce/CommerceSettings.vue'),
                meta: {
                    title: '商城設定',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'commerce/shipping-rules',
                name: 'ClientCommerceShippingRules',
                component: () => import('@/views/client/commerce/CommerceShippingRules.vue'),
                meta: {
                    title: '運費規則',
                    accountType: ['client']
                },
            },
            {
                path: 'commerce/promotion-rules',
                name: 'ClientCommercePromotionRules',
                component: () => import('@/views/client/commerce/CommercePromotionRules.vue'),
                meta: {
                    title: '促銷規則',
                    accountType: ['client']
                },
            },
            {
                path: 'commerce/b2b-pricing',
                name: 'ClientCommerceB2bPricing',
                component: () => import('@/views/client/commerce/CommerceB2bPricing.vue'),
                meta: {
                    title: 'B2B 價格',
                    accountType: ['client']
                },
            },
            {
                path: 'commerce/b2b-pricing/:productId',
                name: 'ClientCommerceB2bPricingProduct',
                component: () => import('@/views/client/commerce/CommerceB2bPricingProduct.vue'),
                meta: {
                    title: 'B2B 價格設定',
                    accountType: ['client']
                },
            },
            {
                path: 'commerce/halls',
                name: 'ClientCommerceHalls',
                component: () => import('@/views/client/commerce/HallsManagement.vue'),
                meta: {
                    title: '館別管理',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'commerce/halls/create',
                name: 'ClientCommerceHallCreate',
                component: () => import('@/views/client/commerce/halls/Form.vue'),
                meta: {
                    title: '新增館別',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'commerce/halls/:hallId/edit',
                name: 'ClientCommerceHallEdit',
                component: () => import('@/views/client/commerce/halls/Form.vue'),
                meta: {
                    title: '編輯館別',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'commerce/halls/:hallId/products',
                name: 'ClientCommerceHallProducts',
                component: () => import('@/views/client/commerce/halls/Products.vue'),
                meta: {
                    title: '館別商品',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'commerce/banners',
                name: 'ClientCommerceBanners',
                component: () => import('@/views/client/commerce/BannersManagement.vue'),
                meta: {
                    title: 'Banner 管理',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'commerce/banners/create',
                name: 'ClientCommerceBannerCreate',
                component: () => import('@/views/client/commerce/banners/Form.vue'),
                meta: {
                    title: '新增 Banner',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'commerce/banners/:bannerId/edit',
                name: 'ClientCommerceBannerEdit',
                component: () => import('@/views/client/commerce/banners/Form.vue'),
                meta: {
                    title: '編輯 Banner',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'commerce/pages',
                name: 'ClientCommercePages',
                component: () => import('@/views/client/commerce/StorefrontPages.vue'),
                meta: {
                    title: '前台頁面管理',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'commerce/pages/create',
                name: 'ClientCommercePageCreate',
                component: () => import('@/views/client/commerce/storefront-pages/Form.vue'),
                meta: {
                    title: '新增前台頁面',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            {
                path: 'commerce/pages/:slug/edit',
                name: 'ClientCommercePageEdit',
                component: () => import('@/views/client/commerce/storefront-pages/Form.vue'),
                meta: {
                    title: '編輯前台頁面',
                    accountType: ['client'], accountRole: ['client_owner']
                },
            },
            // 倉庫管理
            {
                path: 'warehouses',
                name: 'ClientWarehouses',
                component: () => import('@/views/client/warehouses/Index.vue'),
                meta: {
                    title: '倉庫管理',
                    accountType: ['client']
                },
            },
            // 進貨管理
            {
                path: 'inventory/purchase-orders',
                name: 'ClientPurchaseOrders',
                component: () => import('@/views/client/inventory/purchase-orders/Index.vue'),
                meta: {
                    title: '進貨管理',
                    accountType: ['client']
                },
            },
            {
                path: 'inventory/purchase-orders/create',
                name: 'ClientPurchaseOrderCreate',
                component: () => import('@/views/client/inventory/purchase-orders/Create.vue'),
                meta: {
                    title: '新增進貨單',
                    accountType: ['client']
                },
            },
            {
                path: 'inventory/purchase-orders/:id',
                name: 'ClientPurchaseOrderDetail',
                component: () => import('@/views/client/inventory/purchase-orders/Detail.vue'),
                meta: {
                    title: '進貨單詳情',
                    accountType: ['client']
                },
            },
            // 調撥管理
            {
                path: 'inventory/transfer-orders',
                name: 'ClientTransferOrders',
                component: () => import('@/views/client/inventory/transfer-orders/Index.vue'),
                meta: {
                    title: '調撥管理',
                    accountType: ['client']
                },
            },
            {
                path: 'inventory/transfer-orders/create',
                name: 'ClientTransferOrderCreate',
                component: () => import('@/views/client/inventory/transfer-orders/Create.vue'),
                meta: {
                    title: '新增調撥單',
                    accountType: ['client']
                },
            },
            {
                path: 'inventory/transfer-orders/:id',
                name: 'ClientTransferOrderDetail',
                component: () => import('@/views/client/inventory/transfer-orders/Detail.vue'),
                meta: {
                    title: '調撥單詳情',
                    accountType: ['client']
                },
            },
            // 庫存查詢
            {
                path: 'inventory/overview',
                name: 'ClientInventoryOverview',
                component: () => import('@/views/client/inventory/Overview.vue'),
                meta: {
                    title: '庫存總覽',
                    accountType: ['client']
                },
            },
            {
                path: 'inventory/transactions',
                name: 'ClientInventoryTransactions',
                component: () => import('@/views/client/inventory/Transactions.vue'),
                meta: {
                    title: '庫存異動紀錄',
                    accountType: ['client']
                },
            },
            // Wixtar 整合已併入系統參數（/client/settings?tab=wixtar）
            {
                path: 'integrations/wixtar',
                name: 'ClientWixtarConfig',
                redirect: { name: 'ClientSystemSettings', query: { tab: 'wixtar' } },
            },
            {
                path: 'integrations/wixtar/edit',
                name: 'ClientWixtarConfigEdit',
                component: () => import('@/views/client/integrations/wixtar/Edit.vue'),
                meta: {
                    title: 'Wixtar 整合設定',
                    accountType: ['client']
                },
            },
            {
                path: 'integrations/wixtar/sync-logs',
                name: 'ClientWixtarSyncLogs',
                component: () => import('@/views/client/integrations/wixtar/SyncLogs.vue'),
                meta: {
                    title: 'Wixtar 同步記錄',
                    accountType: ['client']
                },
            },
            {
                path: 'orders/unbound',
                name: 'ClientOrdersUnbound',
                component: () => import('@/views/client/orders/Index.vue'),
                meta: {
                    title: '未綁定訂單',
                    accountType: ['client']
                },
            },
            {
                path: 'orders/:id',
                name: 'ClientOrderDetail',
                component: () => import('@/views/client/orders/Detail.vue'),
                meta: {
                    title: '訂單詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'member-tiers',
                name: 'ClientMemberTiers',
                component: () => import('@/views/client/member-tiers/Index.vue'),
                meta: {
                    title: '會員等級管理',
                    accountType: ['client']
                },
            },
            {
                path: 'member-tiers/create',
                name: 'CreateMemberTier',
                component: () => import('@/views/client/member-tiers/Create.vue'),
                meta: {
                    title: '新增會員等級',
                    accountType: ['client']
                },
            },
            {
                path: 'member-tiers/:id/edit',
                name: 'EditMemberTier',
                component: () => import('@/views/client/member-tiers/Create.vue'),
                meta: {
                    title: '編輯會員等級',
                    accountType: ['client']
                },
            },
            {
                path: 'batch-operations',
                name: 'ClientBatchOperations',
                component: () => import('@/views/client/batch-operations/Index.vue'),
                meta: {
                    title: '批次操作管理',
                    accountType: ['client']
                },
            },
            {
                path: 'batch-operations/create',
                name: 'CreateBatchOperation',
                component: () => import('@/views/client/batch-operations/Create.vue'),
                meta: {
                    title: '建立批次操作',
                    accountType: ['client']
                },
            },
            {
                path: 'batch-operations/:batchId',
                name: 'BatchOperationDetail',
                component: () => import('@/views/client/batch-operations/Detail.vue'),
                meta: {
                    title: '批次操作詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'stores',
                name: 'ClientStores',
                component: () => import('@/views/client/stores/Index.vue'),
                meta: {
                    title: '門市管理',
                    accountType: ['client']
                },
            },
            {
                path: 'stores/create',
                name: 'ClientStoreCreate',
                component: () => import('@/views/client/stores/Create.vue'),
                meta: {
                    title: '新增門市',
                    accountType: ['client']
                },
            },
            {
                path: 'stores/:id',
                name: 'ClientStoreDetail',
                component: () => import('@/views/client/stores/Detail.vue'),
                meta: {
                    title: '門市詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'stores/:id/edit',
                name: 'ClientStoreEdit',
                component: () => import('@/views/client/stores/Create.vue'),
                meta: {
                    title: '編輯門市',
                    accountType: ['client']
                },
            },
            {
                path: 'stores/:id/hours',
                name: 'ClientStoreHours',
                component: () => import('@/views/client/stores/Hours.vue'),
                meta: {
                    title: '營業時間設定',
                    accountType: ['client']
                },
            },
            {
                path: 'tags',
                name: 'ClientTags',
                component: () => import('@/views/client/tags/Index.vue'),
                meta: {
                    title: '標籤管理',
                    accountType: ['client']
                },
            },
            {
                path: 'tags/create',
                name: 'ClientTagCreate',
                component: () => import('@/views/client/tags/Create.vue'),
                meta: {
                    title: '新增標籤',
                    accountType: ['client']
                },
            },
            {
                path: 'tags/:id/edit',
                name: 'ClientTagEdit',
                component: () => import('@/views/client/tags/Create.vue'),
                meta: {
                    title: '編輯標籤',
                    accountType: ['client']
                },
            },
            // 數據分析
            {
                path: 'analytics/rfm',
                name: 'ClientRfmAnalysis',
                component: () => import('@/views/client/analytics/RfmAnalysis.vue'),
                meta: {
                    title: 'RFM 分析',
                    accountType: ['client']
                },
            },
            {
                path: 'analytics/rfm/dashboard',
                name: 'ClientRfmDashboard',
                component: () => import('@/views/client/analytics/RfmDashboard.vue'),
                meta: {
                    title: 'RFM Dashboard',
                    accountType: ['client']
                },
            },
            {
                path: 'analytics/clv',
                name: 'ClientClvAnalysis',
                component: () => import('@/views/client/analytics/ClvAnalysis.vue'),
                meta: {
                    title: 'CLV 分析',
                    accountType: ['client']
                },
            },
            {
                path: 'analytics/churn',
                name: 'ClientChurnAnalysis',
                component: () => import('@/views/client/analytics/ChurnAnalysis.vue'),
                meta: {
                    title: '流失預警',
                    accountType: ['client']
                },
            },
            {
                path: 'analytics/activity',
                name: 'ClientActivityAnalysis',
                component: () => import('@/views/client/analytics/ActivityAnalysis.vue'),
                meta: {
                    title: '活躍度分析',
                    accountType: ['client']
                },
            },
            {
                path: 'analytics/marketing-campaigns',
                name: 'ClientMarketingCampaignOverview',
                component: () => import('@/views/client/analytics/MarketingCampaignOverview.vue'),
                meta: {
                    title: '跨活動成效報表',
                    accountType: ['client']
                },
            },
            // 營運報表
            {
                path: 'reports/sales',
                name: 'ClientSalesReport',
                component: () => import('@/views/client/reports/SalesReport.vue'),
                meta: {
                    title: '門市業績報表',
                    accountType: ['client']
                },
            },
            {
                path: 'reports/products',
                name: 'ClientProductRanking',
                component: () => import('@/views/client/reports/ProductRanking.vue'),
                meta: {
                    title: '熱賣商品排行',
                    accountType: ['client']
                },
            },
            {
                path: 'reports/time-analysis',
                name: 'ClientTimeAnalysis',
                component: () => import('@/views/client/reports/TimeAnalysis.vue'),
                meta: {
                    title: '時段分析',
                    accountType: ['client']
                },
            },
            {
                path: 'reports/campaigns',
                name: 'ClientCampaignPerformance',
                component: () => import('@/views/client/reports/CampaignPerformance.vue'),
                meta: {
                    title: '優惠券活動成效',
                    accountType: ['client']
                },
            },
            {
                path: 'reports/line-stats',
                name: 'ClientLineStats',
                component: () => import('@/views/client/reports/LineStats.vue'),
                meta: {
                    title: 'LINE 推播統計',
                    accountType: ['client']
                },
            },
            {
                path: 'auto-tag-rules',
                name: 'ClientAutoTagRules',
                component: () => import('@/views/marketing/auto-tag-rules/Index.vue'),
                meta: {
                    title: '自動貼標規則',
                    accountType: ['client']
                },
            },
            {
                path: 'auto-tag-rules/create',
                name: 'ClientAutoTagRuleCreate',
                component: () => import('@/views/marketing/auto-tag-rules/Form.vue'),
                meta: {
                    title: '新增自動貼標規則',
                    accountType: ['client']
                },
            },
            {
                path: 'auto-tag-rules/edit/:id',
                name: 'ClientAutoTagRuleEdit',
                component: () => import('@/views/marketing/auto-tag-rules/Form.vue'),
                meta: {
                    title: '編輯自動貼標規則',
                    accountType: ['client']
                },
            },
            {
                path: 'audience-rules',
                name: 'ClientAudienceRules',
                component: () => import('@/views/marketing/audience-rules/Index.vue'),
                meta: {
                    title: '受眾規則管理',
                    accountType: ['client']
                },
            },
            {
                path: 'audience-rules/create',
                name: 'ClientAudienceRuleCreate',
                component: () => import('@/views/marketing/audience-rules/Form.vue'),
                meta: {
                    title: '新增受眾規則',
                    accountType: ['client']
                },
            },
            {
                path: 'audience-rules/edit/:id',
                name: 'ClientAudienceRuleEdit',
                component: () => import('@/views/marketing/audience-rules/Form.vue'),
                meta: {
                    title: '編輯受眾規則',
                    accountType: ['client']
                },
            },
            // 自動回應管理
            {
                path: 'auto-reply/rules',
                name: 'AutoReplyRules',
                component: () => import('@/views/marketing/auto-reply/rules/Index.vue'),
                meta: {
                    title: '關鍵字規則',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'auto-reply/rules/create',
                name: 'AutoReplyRuleCreate',
                component: () => import('@/views/marketing/auto-reply/rules/Editor.vue'),
                meta: {
                    title: '新增關鍵字規則',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'auto-reply/rules/:id/edit',
                name: 'AutoReplyRuleEdit',
                component: () => import('@/views/marketing/auto-reply/rules/Editor.vue'),
                meta: {
                    title: '編輯關鍵字規則',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'auto-reply/postback-rules/create',
                name: 'PostbackRuleCreate',
                component: () => import('@/views/marketing/auto-reply/rules/PostbackEditor.vue'),
                meta: {
                    title: '新增 Postback 規則',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'auto-reply/postback-rules/:id/edit',
                name: 'PostbackRuleEdit',
                component: () => import('@/views/marketing/auto-reply/rules/PostbackEditor.vue'),
                meta: {
                    title: '編輯 Postback 規則',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'auto-reply/faqs',
                name: 'AutoReplyFaqs',
                component: () => import('@/views/marketing/auto-reply/faqs/Index.vue'),
                meta: {
                    title: '常見問題',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'auto-reply/faqs/create',
                name: 'AutoReplyFaqCreate',
                component: () => import('@/views/marketing/auto-reply/faqs/Form.vue'),
                meta: {
                    title: '新增 FAQ',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'auto-reply/faqs/:id/edit',
                name: 'AutoReplyFaqEdit',
                component: () => import('@/views/marketing/auto-reply/faqs/Form.vue'),
                meta: {
                    title: '編輯 FAQ',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'auto-reply/business-hours',
                name: 'AutoReplyBusinessHours',
                component: () => import('@/views/marketing/auto-reply/business-hours/Index.vue'),
                meta: {
                    title: '時段控制',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'auto-reply/statistics',
                name: 'AutoReplyStatistics',
                component: () => import('@/views/marketing/auto-reply/statistics/Index.vue'),
                meta: {
                    title: '自動回應成效',
                    accountType: ['system', 'platform', 'client']
                },
            },
            // 抽獎工具管理
            {
                path: 'lottery',
                name: 'LotteryConfigList',
                component: () => import('@/views/client/lottery/Index.vue'),
                meta: {
                    title: '抽獎工具管理',
                    accountType: ['client']
                },
            },
            {
                path: 'lottery/create',
                name: 'CreateLotteryConfig',
                component: () => import('@/views/client/lottery/Create.vue'),
                meta: {
                    title: '新增抽獎配置',
                    accountType: ['client']
                },
            },
            {
                path: 'lottery/:id/edit',
                name: 'EditLotteryConfig',
                component: () => import('@/views/client/lottery/Create.vue'),
                meta: {
                    title: '編輯抽獎配置',
                    accountType: ['client']
                },
            },
            // 簽到活動管理
            {
                path: 'checkin',
                name: 'CheckinConfigList',
                component: () => import('@/views/client/checkin/Index.vue'),
                meta: {
                    title: '簽到活動管理',
                    accountType: ['client']
                },
            },
            {
                path: 'checkin/create',
                name: 'CreateCheckinConfig',
                component: () => import('@/views/client/checkin/Create.vue'),
                meta: {
                    title: '新增簽到配置',
                    accountType: ['client']
                },
            },
            {
                path: 'checkin/:id/edit',
                name: 'EditCheckinConfig',
                component: () => import('@/views/client/checkin/Create.vue'),
                meta: {
                    title: '編輯簽到配置',
                    accountType: ['client']
                },
            },
            {
                path: 'questionnaires',
                name: 'QuestionnaireList',
                component: () => import('@/views/client/questionnaires/Index.vue'),
                meta: { title: '問卷管理', accountType: ['client'] }
            },
            {
                path: 'questionnaires/create',
                name: 'CreateQuestionnaire',
                component: () => import('@/views/client/questionnaires/Form.vue'),
                meta: { title: '新增問卷', accountType: ['client'] }
            },
            {
                path: 'questionnaires/:id/edit',
                name: 'EditQuestionnaire',
                component: () => import('@/views/client/questionnaires/Form.vue'),
                meta: { title: '編輯問卷', accountType: ['client'] }
            },
            {
                path: 'questionnaires/:id/responses',
                name: 'QuestionnaireResponses',
                component: () => import('@/views/client/questionnaires/Responses.vue'),
                meta: { title: '問卷回答', accountType: ['client'] }
            },
            {
                path: 'questionnaires/:id/stats',
                name: 'QuestionnaireStats',
                component: () => import('@/views/client/questionnaires/Stats.vue'),
                meta: { title: '問卷統計', accountType: ['client'] }
            },
            // 集章活動管理
            {
                path: 'stamps',
                name: 'StampCampaignList',
                component: () => import('@/views/client/stamps/Index.vue'),
                meta: {
                    title: '集章活動管理',
                    accountType: ['client']
                },
            },
            {
                path: 'stamps/create',
                name: 'CreateStampCampaign',
                component: () => import('@/views/client/stamps/Create.vue'),
                meta: {
                    title: '新增集章活動',
                    accountType: ['client']
                },
            },
            {
                path: 'stamps/:id/edit',
                name: 'EditStampCampaign',
                component: () => import('@/views/client/stamps/Create.vue'),
                meta: {
                    title: '編輯集章活動',
                    accountType: ['client']
                },
            },
            // 分享換好康活動管理
            {
                path: 'share-reward',
                name: 'ShareRewardCampaignList',
                component: () => import('@/views/client/share-reward/Index.vue'),
                meta: {
                    title: '分享換好康',
                    accountType: ['client']
                },
            },
            {
                path: 'share-reward/create',
                name: 'CreateShareRewardCampaign',
                component: () => import('@/views/client/share-reward/Create.vue'),
                meta: {
                    title: '新增分享換好康活動',
                    accountType: ['client']
                },
            },
            {
                path: 'share-reward/:id/edit',
                name: 'EditShareRewardCampaign',
                component: () => import('@/views/client/share-reward/Create.vue'),
                meta: {
                    title: '編輯分享換好康活動',
                    accountType: ['client']
                },
            },
            {
                path: 'follow-marketing',
                name: 'FollowMarketingCampaignList',
                component: () => import('@/views/client/follow-marketing/Index.vue'),
                meta: {
                    title: '加好友行銷',
                    accountType: ['client']
                },
            },
            {
                path: 'follow-marketing/create',
                name: 'CreateFollowMarketingCampaign',
                component: () => import('@/views/client/follow-marketing/Create.vue'),
                meta: {
                    title: '新增加好友行銷',
                    accountType: ['client']
                },
            },
            {
                path: 'follow-marketing/:id/edit',
                name: 'EditFollowMarketingCampaign',
                component: () => import('@/views/client/follow-marketing/Create.vue'),
                meta: {
                    title: '編輯加好友行銷',
                    accountType: ['client']
                },
            },
            {
                path: 'marketing-campaigns',
                name: 'MarketingCampaignList',
                component: () => import('@/views/client/marketing-campaigns/Index.vue'),
                meta: {
                    title: '行銷活動流程',
                    accountType: ['client']
                },
            },
            {
                path: 'reward-reissues',
                name: 'RewardReissueList',
                component: () => import('@/views/client/reward-reissues/Index.vue'),
                meta: {
                    title: '獎勵補發',
                    accountType: ['client']
                },
            },
            {
                path: 'marketing-campaigns/create',
                name: 'CreateMarketingCampaign',
                component: () => import('@/views/client/marketing-campaigns/Create.vue'),
                meta: {
                    title: '新增行銷活動',
                    accountType: ['client']
                },
            },
            {
                path: 'marketing-campaigns/create-from-playbook',
                name: 'CreateMarketingCampaignFromPlaybook',
                component: () => import('@/views/client/marketing-campaigns/PlaybookCreate.vue'),
                meta: {
                    title: '從劇本建立活動',
                    accountType: ['client']
                },
            },
            {
                path: 'marketing-campaigns/:id/edit',
                name: 'EditMarketingCampaign',
                component: () => import('@/views/client/marketing-campaigns/Edit.vue'),
                meta: {
                    title: '編輯行銷活動',
                    accountType: ['client']
                },
            },
            {
                path: 'smart-marketing',
                name: 'SmartMarketingList',
                component: () => import('@/views/client/smart-marketing/Index.vue'),
                meta: {
                    title: '智能行銷方案',
                    accountType: ['client']
                },
            },
            {
                path: 'smart-marketing/wizard',
                name: 'SmartMarketingWizard',
                component: () => import('@/views/client/smart-marketing/Wizard.vue'),
                meta: {
                    title: '智能行銷精靈',
                    accountType: ['client']
                },
            },
            // 點數管理
            {
                path: 'points/transactions',
                name: 'PointsTransactions',
                component: () => import('@/views/client/points/transactions/Index.vue'),
                meta: {
                    title: '點數交易記錄',
                    accountType: ['client']
                },
            },
            {
                path: 'points/adjust',
                name: 'PointsAdjust',
                component: () => import('@/views/client/points/adjust/Index.vue'),
                meta: {
                    title: '手動調整點數',
                    accountType: ['client']
                },
            },
            {
                path: 'points/batch-issue/create',
                name: 'PointsBatchIssueCreate',
                component: () => import('@/views/client/points/batch-issue/Create.vue'),
                meta: {
                    title: '批次發放點數',
                    accountType: ['client']
                },
            },
            {
                path: 'points/batch-issue/:batchId',
                name: 'PointsBatchIssueDetail',
                component: () => import('@/views/client/points/batch-issue/Detail.vue'),
                meta: {
                    title: '批次發放點數詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'points/batch-issue',
                name: 'PointsBatchIssue',
                component: () => import('@/views/client/points/batch-issue/Index.vue'),
                meta: {
                    title: '批次發放點數',
                    accountType: ['client']
                },
            },
            {
                path: 'points/expiry-config',
                name: 'PointsExpiryConfig',
                component: () => import('@/views/client/points/expiry-config/Index.vue'),
                meta: {
                    title: '點數過期設定',
                    accountType: ['client']
                },
            },
            // 自動發送機制
            {
                path: 'auto-send/birthday-gift',
                name: 'BirthdayGiftModuleList',
                component: () => import('@/views/client/auto-send/birthday-gift/ModuleList.vue'),
                meta: {
                    title: '生日禮模組清單',
                    accountType: ['client']
                },
            },
            {
                path: 'auto-send/birthday-gift/:moduleId/edit',
                name: 'BirthdayGiftConfigEdit',
                component: () => import('@/views/client/auto-send/birthday-gift/Index.vue'),
                meta: {
                    title: '生日禮設定',
                    accountType: ['client']
                },
            },
            {
                path: 'auto-send/anniversary-gift',
                name: 'AnniversaryGiftModuleList',
                component: () => import('@/views/client/auto-send/anniversary-gift/ModuleList.vue'),
                meta: {
                    title: '週年禮模組清單',
                    accountType: ['client']
                },
            },
            {
                path: 'auto-send/anniversary-gift/:moduleId/edit',
                name: 'AnniversaryGiftConfigEdit',
                component: () => import('@/views/client/auto-send/anniversary-gift/Index.vue'),
                meta: {
                    title: '週年禮設定',
                    accountType: ['client']
                },
            },
            {
                path: 'auto-send/registration-gift',
                name: 'RegistrationGiftCampaignList',
                component: () => import('@/views/client/auto-send/registration-gift/CampaignList.vue'),
                meta: {
                    title: '註冊禮',
                     accountType: ['client']
                },
            },
            {
                path: 'auto-send/registration-gift/:moduleId/edit',
                name: 'RegistrationGiftConfigEdit',
                component: () => import('@/views/client/auto-send/registration-gift/Index.vue'),
                meta: {
                    title: '註冊禮設定',
                    accountType: ['client']
                },
            },
            {
                path: 'auto-send/tier-upgrade-gift',
                name: 'TierUpgradeGiftConfig',
                component: () => import('@/views/client/auto-send/tier-upgrade-gift/Index.vue'),
                meta: {
                    title: '升等禮',
                    accountType: ['client']
                },
            },
            {
                path: 'auto-send/tier-upgrade-gift/edit',
                redirect: { name: 'TierUpgradeGiftConfig' },
            },
            // 通知範本管理
            {
                path: 'notification-templates',
                name: 'NotificationTemplates',
                component: () => import('@/views/client/notification-templates/Index.vue'),
                meta: {
                    title: '通知範本管理',
                    accountType: ['client']
                },
            },
            {
                path: 'notification-templates/create',
                name: 'NotificationTemplateCreate',
                component: () => import('@/views/client/notification-templates/Form.vue'),
                meta: {
                    title: '新增通知範本',
                    accountType: ['client']
                },
            },
            {
                path: 'notification-templates/:id/edit',
                name: 'NotificationTemplateEdit',
                component: () => import('@/views/client/notification-templates/Form.vue'),
                meta: {
                    title: '編輯通知範本',
                    accountType: ['client']
                },
            },
            // 訂閱與交易記錄
            {
                path: 'subscription',
                name: 'ClientSubscription',
                component: () => import('@/views/client/subscription/Index.vue'),
                meta: {
                    title: '訂閱與交易記錄',
                    accountType: ['client']
                },
            },
            // ChatMate AI 客服
            {
                path: 'chatmate/knowledge',
                name: 'ChatMateKnowledge',
                component: () => import('@/views/client/chatmate/knowledge/Index.vue'),
                meta: {
                    title: '知識庫管理',
                    accountType: ['client']
                },
            },
            {
                path: 'chatmate/knowledge/upload',
                name: 'ChatMateKnowledgeUpload',
                component: () => import('@/views/client/chatmate/knowledge/Upload.vue'),
                meta: {
                    title: '上傳知識庫',
                    accountType: ['client']
                },
            },
            {
                path: 'chatmate/knowledge/:id',
                name: 'ChatMateKnowledgeDetail',
                component: () => import('@/views/client/chatmate/knowledge/Detail.vue'),
                meta: {
                    title: '知識庫詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'chatmate/conversations',
                name: 'ChatMateConversations',
                component: () => import('@/views/client/chatmate/conversations/Index.vue'),
                meta: {
                    title: '對話歷史',
                    accountType: ['client']
                },
            },
            {
                path: 'chatmate/conversations/:sessionId',
                name: 'ChatMateConversationDetail',
                component: () => import('@/views/client/chatmate/conversations/Detail.vue'),
                meta: {
                    title: '對話詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'chatmate/quota',
                name: 'ChatMateQuota',
                component: () => import('@/views/client/chatmate/quota/Index.vue'),
                meta: {
                    title: '配額設定',
                    accountType: ['client']
                },
            },
            {
                path: 'chatmate/usage',
                name: 'ChatMateUsage',
                component: () => import('@/views/client/chatmate/usage/Index.vue'),
                meta: {
                    title: '使用量統計',
                    accountType: ['client']
                },
            },
            {
                path: 'chatmate/settings',
                name: 'ChatMateSettings',
                component: () => import('@/views/client/chatmate/settings/Index.vue'),
                meta: {
                    title: 'AI 設定',
                    accountType: ['client']
                },
            },
            {
                path: 'chatmate/subscription',
                name: 'ChatMateSubscription',
                component: () => import('@/views/client/chatmate/subscription/Index.vue'),
                meta: {
                    title: '訂閱管理',
                    accountType: ['client']
                },
            },
            {
                path: 'chatdesk',
                name: 'ChatDeskDesk',
                component: () => import('@/views/client/chatdesk/Index.vue'),
                meta: {
                    title: 'ChatDesk 工作台',
                    accountType: ['client']
                },
            },
            {
                path: 'chatdesk/subscription',
                name: 'ChatDeskSubscription',
                component: () => import('@/views/client/chatdesk/subscription/Index.vue'),
                meta: {
                    title: 'ChatDesk 訂閱',
                    accountType: ['client']
                },
            },
            {
                path: 'chatdesk/canned-replies',
                name: 'ChatDeskCannedReplies',
                component: () => import('@/views/client/chatdesk/settings/Index.vue'),
                meta: {
                    title: 'ChatDesk 罐頭回覆',
                    accountType: ['client']
                },
            },
            {
                path: 'chatdesk/canned-replies/create',
                name: 'ChatDeskCannedReplyCreate',
                component: () => import('@/views/client/chatdesk/canned-replies/Editor.vue'),
                meta: {
                    title: '新增 ChatDesk 罐頭回覆',
                    accountType: ['client']
                },
            },
            {
                path: 'chatdesk/canned-replies/:id/edit',
                name: 'ChatDeskCannedReplyEdit',
                component: () => import('@/views/client/chatdesk/canned-replies/Editor.vue'),
                meta: {
                    title: '編輯 ChatDesk 罐頭回覆',
                    accountType: ['client']
                },
            },
            {
                path: 'chatdesk/settings',
                name: 'ChatDeskSettings',
                component: () => import('@/views/client/chatdesk/settings/Index.vue'),
                meta: {
                    title: 'ChatDesk 設定',
                    accountType: ['client']
                },
            },
            {
                path: 'salon',
                name: 'SalonOverview',
                component: () => import('@/views/client/salon/Overview.vue'),
                meta: { title: '預約總覽', accountType: ['client'] },
            },
            {
                path: 'salon/stylists',
                name: 'SalonStylists',
                component: () => import('@/views/client/salon/ManagementList.vue'),
                props: { section: 'stylists' },
                meta: { title: '設計師管理', accountType: ['client'] },
            },
            {
                path: 'salon/stylists/create',
                name: 'SalonStylistCreate',
                component: () => import('@/views/client/salon/StylistForm.vue'),
                meta: { title: '新增設計師', accountType: ['client'] },
            },
            {
                path: 'salon/stylists/:id/edit',
                name: 'SalonStylistEdit',
                component: () => import('@/views/client/salon/StylistForm.vue'),
                meta: { title: '編輯設計師', accountType: ['client'] },
            },
            {
                path: 'salon/services',
                name: 'SalonServices',
                component: () => import('@/views/client/salon/ManagementList.vue'),
                props: { section: 'services' },
                meta: { title: '服務項目管理', accountType: ['client'] },
            },
            {
                path: 'salon/services/create',
                name: 'SalonServiceCreate',
                component: () => import('@/views/client/salon/ServiceForm.vue'),
                meta: { title: '新增服務項目', accountType: ['client'] },
            },
            {
                path: 'salon/services/:id/edit',
                name: 'SalonServiceEdit',
                component: () => import('@/views/client/salon/ServiceForm.vue'),
                meta: { title: '編輯服務項目', accountType: ['client'] },
            },
            {
                path: 'salon/schedules',
                name: 'SalonSchedules',
                component: () => import('@/views/client/salon/ManagementList.vue'),
                props: { section: 'schedules' },
                meta: { title: '排班管理', accountType: ['client'] },
            },
            {
                path: 'salon/schedules/store/:storeId',
                name: 'SalonStoreSchedules',
                component: () => import('@/views/client/salon/StoreSchedules.vue'),
                meta: { title: '門市班表', accountType: ['client'] },
            },
            {
                path: 'salon/schedules/create',
                name: 'SalonScheduleCreate',
                component: () => import('@/views/client/salon/ScheduleForm.vue'),
                meta: { title: '新增班表', accountType: ['client'] },
            },
            {
                path: 'salon/schedules/:id/edit',
                name: 'SalonScheduleEdit',
                component: () => import('@/views/client/salon/ScheduleForm.vue'),
                meta: { title: '編輯班表', accountType: ['client'] },
            },
            {
                path: 'salon/exceptions',
                name: 'SalonExceptions',
                component: () => import('@/views/client/salon/ManagementList.vue'),
                props: { section: 'exceptions' },
                meta: { title: '休假與預約限制', accountType: ['client'] },
            },
            {
                path: 'salon/exceptions/create',
                name: 'SalonExceptionCreate',
                component: () => import('@/views/client/salon/ExceptionForm.vue'),
                meta: { title: '新增休假或預約限制', accountType: ['client'] },
            },
            {
                path: 'salon/exceptions/:type/:id/edit',
                name: 'SalonExceptionEdit',
                component: () => import('@/views/client/salon/ExceptionForm.vue'),
                meta: { title: '編輯休假或預約限制', accountType: ['client'] },
            },
            {
                path: 'salon/reservations/create',
                name: 'SalonReservationCreate',
                component: () => import('@/views/client/salon/ReservationForm.vue'),
                meta: { title: '新增美髮預約', accountType: ['client'] },
            },
            {
                path: 'salon/reservations/:id/edit',
                name: 'SalonReservationEdit',
                component: () => import('@/views/client/salon/ReservationForm.vue'),
                meta: { title: '編輯美髮預約', accountType: ['client'] },
            },
            {
                path: 'salon/settings',
                name: 'SalonSettings',
                component: () => import('@/views/client/salon/ManagementList.vue'),
                props: { section: 'settings' },
                meta: { title: '預約設定', accountType: ['client'] },
            },
            {
                path: 'salon/settings/:storeId/edit',
                name: 'SalonSettingsEdit',
                component: () => import('@/views/client/salon/SettingsForm.vue'),
                meta: { title: '編輯預約設定', accountType: ['client'] },
            },
            {
                path: 'support/tickets',
                name: 'SupportTickets',
                component: () => import('@/views/client/support/tickets/Index.vue'),
                meta: {
                    title: '客服工單',
                    accountType: ['client']
                },
            },
            {
                path: 'support/tickets/create',
                name: 'SupportTicketCreate',
                component: () => import('@/views/client/support/tickets/Create.vue'),
                meta: {
                    title: '新增客服工單',
                    accountType: ['client']
                },
            },
            {
                path: 'support/tickets/:id',
                name: 'SupportTicketDetail',
                component: () => import('@/views/client/support/tickets/Detail.vue'),
                meta: {
                    title: '客服工單詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'support/categories',
                name: 'SupportTicketCategories',
                component: () => import('@/views/client/support/categories/Index.vue'),
                meta: {
                    title: '工單分類',
                    accountType: ['client']
                },
            },
            {
                path: 'points/earning-rules/normal',
                name: 'PointsEarningRulesNormal',
                component: () => import('@/views/client/points/earning-rules/normal/Index.vue'),
                meta: {
                    title: '常態給點規則設定',
                    accountType: ['client']
                },
            },
            {
                path: 'points/earning-rules/time-range',
                name: 'PointsEarningRulesTimeRange',
                component: () => import('@/views/client/points/earning-rules/time-range/Index.vue'),
                meta: {
                    title: '時間區間給點規則',
                    accountType: ['client']
                },
            },
            {
                path: 'points/earning-rules/time-range/create',
                name: 'PointsEarningRulesTimeRangeCreate',
                component: () => import('@/views/client/points/earning-rules/time-range/Create.vue'),
                meta: {
                    title: '新增時間區間給點規則',
                    accountType: ['client']
                },
            },
            {
                path: 'points/earning-rules/time-range/:id/edit',
                name: 'PointsEarningRulesTimeRangeEdit',
                component: () => import('@/views/client/points/earning-rules/time-range/Edit.vue'),
                meta: {
                    title: '編輯時間區間給點規則',
                    accountType: ['client']
                },
            },
            {
                path: 'points/earning-rules/time-range/:id/logs',
                name: 'PointsEarningRulesTimeRangeLogs',
                component: () => import('@/views/client/points/earning-rules/time-range/Logs.vue'),
                meta: {
                    title: '規則變更記錄',
                    accountType: ['client']
                },
            },
            // 預約管理
            {
                path: 'reservations',
                name: 'ClientReservations',
                component: () => import('@/views/client/reservations/Index.vue'),
                meta: {
                    title: '訂位管理',
                    accountType: ['client']
                },
            },
            {
                path: 'reservations/create',
                name: 'ClientReservationCreate',
                component: () => import('@/views/client/reservations/Create.vue'),
                meta: {
                    title: '新增訂位',
                    accountType: ['client']
                },
            },
            {
                path: 'reservations/store-settings',
                name: 'ClientReservationStoreSettings',
                component: () => import('@/views/client/reservations/StoreSettings.vue'),
                meta: {
                    title: '訂位參數設定',
                    accountType: ['client']
                },
            },
            {
                path: 'reservations/:id',
                name: 'ClientReservationDetail',
                component: () => import('@/views/client/reservations/Detail.vue'),
                meta: {
                    title: '訂位詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'reservations/:id/edit',
                name: 'ClientReservationEdit',
                component: () => import('@/views/client/reservations/Create.vue'),
                meta: {
                    title: '編輯訂位',
                    accountType: ['client']
                },
            },
            // Google Calendar 設定
            {
                path: 'calendar/settings',
                name: 'ClientCalendarSettings',
                component: () => import('@/views/client/calendar/Settings.vue'),
                meta: {
                    title: 'Google Calendar 設定',
                    accountType: ['client']
                },
            },
            // 座位管理
            {
                path: 'tables',
                name: 'ClientTables',
                component: () => import('@/views/client/tables/Index.vue'),
                meta: {
                    title: '座位管理',
                    accountType: ['client']
                },
            },
            {
                path: 'tables/create',
                name: 'ClientTableCreate',
                component: () => import('@/views/client/tables/Create.vue'),
                meta: {
                    title: '新增座位',
                    accountType: ['client']
                },
            },
            {
                path: 'tables/:id/edit',
                name: 'ClientTableEdit',
                component: () => import('@/views/client/tables/Edit.vue'),
                meta: {
                    title: '編輯座位',
                    accountType: ['client']
                },
            },
            {
                path: 'tables/status',
                name: 'ClientTableStatus',
                component: () => import('@/views/client/tables/Status.vue'),
                meta: {
                    title: '座位使用狀態',
                    accountType: ['client']
                },
            },
            // 候位管理
            {
                path: 'waitlist',
                name: 'ClientWaitlist',
                component: () => import('@/views/client/waitlist/Index.vue'),
                meta: {
                    title: '候位管理',
                    accountType: ['client']
                },
            },
            {
                path: 'waitlist/create',
                name: 'ClientWaitlistCreate',
                component: () => import('@/views/client/waitlist/Create.vue'),
                meta: {
                    title: '登記候位',
                    accountType: ['client']
                },
            },
            // 取貨預約
            {
                path: 'pickup-appointments',
                name: 'ClientPickupAppointments',
                component: () => import('@/views/client/pickup-appointments/Index.vue'),
                meta: {
                    title: '取貨預約',
                    accountType: ['client']
                },
            },
            {
                path: 'pickup-appointments/create',
                name: 'ClientPickupAppointmentCreate',
                component: () => import('@/views/client/pickup-appointments/Create.vue'),
                meta: {
                    title: '新增取貨預約',
                    accountType: ['client']
                },
            },
            {
                path: 'pickup-appointments/:id',
                name: 'ClientPickupAppointmentDetail',
                component: () => import('@/views/client/pickup-appointments/Detail.vue'),
                meta: {
                    title: '取貨預約詳情',
                    accountType: ['client']
                },
            },
            // Google Calendar 設定（作為門市詳情的子頁面）
            {
                path: 'stores/:id/calendar',
                name: 'ClientStoreCalendar',
                component: () => import('@/views/client/stores/Calendar.vue'),
                meta: {
                    title: 'Google Calendar 設定',
                    accountType: ['client']
                },
            },
            // 優惠券管理
            {
                path: 'coupons/templates',
                name: 'CouponTemplates',
                component: () => import('@/views/client/coupons/templates/Index.vue'),
                meta: {
                    title: '優惠券模板列表',
                    accountType: ['client']
                },
            },
            {
                path: 'coupons/templates/create',
                name: 'CreateCouponTemplate',
                component: () => import('@/views/client/coupons/templates/Form.vue'),
                meta: {
                    title: '新增優惠券模板',
                    accountType: ['client']
                },
            },
            {
                path: 'coupons/templates/:id',
                name: 'CouponTemplateDetail',
                component: () => import('@/views/client/coupons/templates/Detail.vue'),
                meta: {
                    title: '優惠券模板詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'coupons/templates/:id/edit',
                name: 'EditCouponTemplate',
                component: () => import('@/views/client/coupons/templates/Form.vue'),
                meta: {
                    title: '編輯優惠券模板',
                    accountType: ['client']
                },
            },
            {
                path: 'coupons/issues/manual',
                name: 'ManualIssueCoupon',
                component: () => import('@/views/client/coupons/issues/ManualIssue.vue'),
                meta: {
                    title: '手動發放優惠券',
                    accountType: ['client']
                },
            },
            {
                path: 'coupons/issues/batch/create',
                name: 'BatchIssueCouponCreate',
                component: () => import('@/views/client/coupons/issues/BatchIssue.vue'),
                meta: {
                    title: '批次發送優惠券',
                    accountType: ['client']
                },
            },
            {
                path: 'coupons/issues/batch/:batchId',
                name: 'BatchCouponIssuanceDetail',
                component: () => import('@/views/client/coupons/batch-issuances/Detail.vue'),
                meta: {
                    title: '批次發送優惠券詳情',
                    accountType: ['client']
                },
                alias: 'coupons/batch-issuances/:batchId',
            },
            {
                path: 'coupons/issues/batch',
                name: 'BatchCouponIssuances',
                component: () => import('@/views/client/coupons/batch-issuances/Index.vue'),
                meta: {
                    title: '批次發送優惠券',
                    accountType: ['client']
                },
                alias: 'coupons/batch-issuances',
            },
            {
                path: 'coupons/issues/:id',
                name: 'CouponIssueDetail',
                component: () => import('@/views/client/coupons/issues/Detail.vue'),
                meta: {
                    title: '優惠券詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'coupons/issues',
                name: 'CouponIssues',
                component: () => import('@/views/client/coupons/issues/Index.vue'),
                meta: {
                    title: '優惠券發放記錄',
                    accountType: ['client']
                },
            },
            {
                path: 'coupons/redeem',
                name: 'CouponRedeem',
                component: () => import('@/views/client/coupons/redeem/Index.vue'),
                meta: {
                    title: '優惠券核銷',
                    accountType: ['client']
                },
            },
            // Wixtar 票券（獨立模組，票券資料直接來自 Wixtar API 不落地）
            {
                path: 'wixtar/tickets',
                name: 'ClientWixtarTicketList',
                component: () => import('@/views/client/wixtar/Index.vue'),
                meta: {
                    title: 'Wixtar 票券管理',
                    accountType: ['client']
                },
            },
            {
                path: 'wixtar/tickets/:ticketId',
                name: 'ClientWixtarTicketDetail',
                component: () => import('@/views/client/wixtar/Detail.vue'),
                meta: {
                    title: 'Wixtar 票券詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'wixtar/tickets/:ticketId/dispatch',
                name: 'ClientWixtarTicketDispatch',
                component: () => import('@/views/client/wixtar/Dispatch.vue'),
                meta: {
                    title: 'Wixtar 派券',
                    accountType: ['client']
                },
            },
            {
                path: 'wixtar/dispatches',
                name: 'ClientWixtarDispatchList',
                component: () => import('@/views/client/wixtar/DispatchList.vue'),
                meta: {
                    title: 'Wixtar 派券紀錄',
                    accountType: ['client']
                },
            },
            {
                path: 'wixtar/dispatches/:batchId',
                name: 'ClientWixtarDispatchDetail',
                component: () => import('@/views/client/wixtar/DispatchDetail.vue'),
                meta: {
                    title: 'Wixtar 派券紀錄詳情',
                    accountType: ['client']
                },
            },
            // 客戶路由下的 403 頁面（使用 MainLayout）
            {
                path: 'forbidden',
                name: 'ClientForbidden',
                component: () => import('@/views/errors/403.vue'),
                meta: { title: '沒有權限' },
            },
            // LINE 官方帳號設定（統一使用客戶路由，系統/平台帳號可協助管理）
            {
                path: 'line/config',
                name: 'ClientLineConfigList',
                component: () => import('@/views/client/line/Index.vue'),
                meta: {
                    title: 'LINE 官方帳號設定',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'line/config/create',
                name: 'ClientCreateLineConfig',
                component: () => import('@/views/client/line/Create.vue'),
                meta: {
                    title: '新增 LINE 設定',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'line/config/:id/edit',
                name: 'ClientEditLineConfig',
                component: () => import('@/views/client/line/Create.vue'),
                meta: {
                    title: '編輯 LINE 設定',
                    accountType: ['system', 'platform', 'client']
                },
            },
            {
                path: 'line/welcome',
                name: 'ClientLineWelcome',
                component: () => import('@/views/client/line/welcome/Index.vue'),
                meta: {
                    title: '加好友歡迎詞',
                    accountType: ['client']
                },
            },
            // LINE QR Code 管理
            {
                path: 'line/qrcode',
                name: 'ClientLineQrcodeList',
                component: () => import('@/views/client/line/qrcode/Index.vue'),
                meta: {
                    title: 'QR Code 管理',
                    accountType: ['client']
                },
            },
            {
                path: 'line/qrcode/create',
                name: 'ClientCreateLineQrcode',
                component: () => import('@/views/client/line/qrcode/Create.vue'),
                meta: {
                    title: '新增 QR Code',
                    accountType: ['client']
                },
            },
            {
                path: 'line/qrcode/:id/edit',
                name: 'ClientEditLineQrcode',
                component: () => import('@/views/client/line/qrcode/Create.vue'),
                meta: {
                    title: '編輯 QR Code',
                    accountType: ['client']
                },
            },
            {
                path: 'line/qrcode/:id',
                name: 'ClientLineQrcodeDetail',
                component: () => import('@/views/client/line/qrcode/Detail.vue'),
                meta: {
                    title: 'QR Code 詳情',
                    accountType: ['client']
                },
            },
            // LINE 短網址管理
            {
                path: 'line/short-url',
                name: 'ClientLineShortUrlList',
                component: () => import('@/views/client/line/short-url/Index.vue'),
                meta: {
                    title: '短網址管理',
                    accountType: ['client']
                },
            },
            {
                path: 'line/short-url/create',
                name: 'ClientCreateLineShortUrl',
                component: () => import('@/views/client/line/short-url/Create.vue'),
                meta: {
                    title: '新增短網址',
                    accountType: ['client']
                },
            },
            {
                path: 'line/short-url/:id/edit',
                name: 'ClientEditLineShortUrl',
                component: () => import('@/views/client/line/short-url/Create.vue'),
                meta: {
                    title: '編輯短網址',
                    accountType: ['client']
                },
            },
            {
                path: 'line/short-url/:id',
                name: 'ClientLineShortUrlDetail',
                component: () => import('@/views/client/line/short-url/Detail.vue'),
                meta: {
                    title: '短網址詳情',
                    accountType: ['client']
                },
            },
            // LINE 好友資料
            {
                path: 'line/bindings',
                name: 'ClientLineBindingList',
                component: () => import('@/views/client/line/bindings/Index.vue'),
                meta: {
                    title: '好友資料',
                    accountType: ['client']
                },
            },
            {
                path: 'line/bindings/:id',
                name: 'ClientLineBindingDetail',
                component: () => import('@/views/client/line/bindings/Detail.vue'),
                meta: {
                    title: '好友綁定詳情',
                    accountType: ['client']
                },
            },
            // LINE 推播管理
            {
                path: 'line/broadcast',
                name: 'ClientLineBroadcastList',
                component: () => import('@/views/client/line/broadcast/Index.vue'),
                meta: {
                    title: 'LINE 推播管理',
                    accountType: ['client']
                },
            },
            {
                path: 'line/broadcast/create',
                name: 'ClientLineBroadcastCreate',
                component: () => import('@/views/client/line/broadcast/Create.vue'),
                meta: {
                    title: '新增推播活動',
                    accountType: ['client']
                },
            },
            {
                path: 'line/broadcast/:id',
                name: 'ClientLineBroadcastDetail',
                component: () => import('@/views/client/line/broadcast/Detail.vue'),
                meta: {
                    title: '推播活動詳情',
                    accountType: ['client']
                },
            },
            {
                path: 'line/broadcast/recurring/:id/edit',
                name: 'ClientLineBroadcastRecurringEdit',
                component: () => import('@/views/client/line/broadcast/Create.vue'),
                meta: {
                    title: '編輯週期推播',
                    recurringEdit: true,
                    accountType: ['client']
                },
            },
            {
                path: 'line/broadcast/:id/edit',
                name: 'ClientLineBroadcastEdit',
                component: () => import('@/views/client/line/broadcast/Create.vue'),
                meta: {
                    title: '編輯推播活動',
                    accountType: ['client']
                },
            },
            // LINE 封鎖記錄（已合併至好友綁定紀錄頁，保留路由做跳轉）
            {
                path: 'line/block-logs',
                name: 'ClientLineBlockLogList',
                redirect: to => ({ name: 'ClientLineBindingList', query: { tab: 'block' } }),
                meta: {
                    title: '封鎖記錄',
                    accountType: ['client']
                },
            },
            // LINE 訊息範本
            {
                path: 'line-msg-templates',
                name: 'LineMsgTemplates',
                component: () => import('@/views/client/line-msg-templates/Index.vue'),
                meta: {
                    title: 'LINE 訊息範本',
                    accountType: ['client']
                },
            },
            {
                path: 'line-msg-templates/create',
                name: 'LineMsgTemplateCreate',
                component: () => import('@/views/client/line-msg-templates/Editor.vue'),
                meta: {
                    title: '新增 LINE 訊息範本',
                    accountType: ['client']
                },
            },
            {
                path: 'line-msg-templates/:id/edit',
                name: 'LineMsgTemplateEdit',
                component: () => import('@/views/client/line-msg-templates/Editor.vue'),
                meta: {
                    title: '編輯 LINE 訊息範本',
                    accountType: ['client']
                },
            },
            // LINE 圖文選單
            {
                path: 'line/rich-menu',
                name: 'ClientLineRichMenuList',
                component: () => import('@/views/client/line/rich-menu/Index.vue'),
                meta: {
                    title: 'LINE 圖文選單',
                    accountType: ['client']
                },
            },
            {
                path: 'line/rich-menu/create',
                name: 'ClientLineRichMenuCreate',
                component: () => import('@/views/client/line/rich-menu/Editor.vue'),
                meta: {
                    title: '新增圖文選單',
                    accountType: ['client']
                },
            },
            {
                path: 'line/rich-menu/:id/edit',
                name: 'ClientLineRichMenuEdit',
                component: () => import('@/views/client/line/rich-menu/Editor.vue'),
                meta: {
                    title: '編輯圖文選單',
                    accountType: ['client']
                },
            },
            // 客戶路由下的 404 頁面（使用 MainLayout）
            {
                path: ':pathMatch(.*)*',
                name: 'ClientNotFound',
                component: () => import('@/views/errors/404.vue'),
                meta: { title: '頁面不存在' },
            },
        ],
    },
    // Dashboard 重定向（根據帳號類型）
    {
        path: '/dashboard',
        redirect: () => {
            const accountType = authService.getAccountType();
            if (accountType === 'system') {
                return '/system/dashboard';
            } else if (accountType === 'platform') {
                return '/platform/dashboard';
            } else if (accountType === 'client') {
                return '/client/dashboard';
            }
            return '/system/login';
        },
    },
    // 受眾規則路徑重定向（相容舊路徑）
    {
        path: '/marketing/audience-rules',
        redirect: '/client/audience-rules',
    },
    {
        path: '/marketing/audience-rules/create',
        redirect: '/client/audience-rules/create',
    },
    {
        path: '/marketing/audience-rules/edit/:id',
        redirect: to => `/client/audience-rules/edit/${to.params.id}`,
    },
    // 預設重定向
    {
        path: '/',
        redirect: '/system/login',
    },
    // 404 頁面（必須放在最後）
    // ECPay 付款結果頁（不需要登入，綠界直接 redirect 到此）
    {
        path: '/payment/result',
        name: 'PaymentResult',
        component: () => import('@/views/payment/Result.vue'),
        meta: { requiresAuth: false, title: '付款結果' },
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: () => import('@/views/errors/404.vue'),
        meta: { requiresAuth: false, title: '頁面不存在' },
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    // 路由切換時自動滾動到頂部
    scrollBehavior(to, from, savedPosition) {
        // 如果有保存的位置（例如瀏覽器後退/前進），恢復該位置
        if (savedPosition) {
            return savedPosition;
        }
        // 否則滾動到頂部
        return { top: 0, left: 0, behavior: 'smooth' };
    },
});

// 路由守衛
router.beforeEach((to, from, next) => {
    // 如果是 404 或 403 頁面，直接放行（不需要認證檢查）
    if (to.name === 'NotFound' || to.name === 'SystemNotFound' || to.name === 'PlatformNotFound' || to.name === 'ClientNotFound' ||
        to.name === 'SystemForbidden' || to.name === 'PlatformForbidden' || to.name === 'ClientForbidden') {
        return next();
    }

    const requiresAuth = to.meta.requiresAuth;
    const isAuthenticated = authService.isAuthenticated();
    const accountType = authService.getAccountType();
    const requiredAccountType = to.meta.accountType;
    const requiredAccountRole = to.meta.accountRole;

    // 如果路由需要認證但使用者未登入
    if (requiresAuth && !isAuthenticated) {
        // 根據目標路由決定導向哪個登入頁
        if (to.path.startsWith('/system')) {
            next('/system/login');
        } else if (to.path.startsWith('/platform')) {
            next('/platform/login');
        } else if (to.path.startsWith('/client')) {
            next('/client/login');
        } else {
            next('/system/login');
        }
        return;
    }

    // 如果使用者已登入但訪問登入頁，導向對應的 Dashboard
    if (!requiresAuth && isAuthenticated) {
        if (accountType === 'system') {
            return next('/system/dashboard');
        } else if (accountType === 'platform') {
            return next('/platform/dashboard');
        } else if (accountType === 'client') {
            return next('/client/dashboard');
        }
        return next();
    }

    // 系統帳號可以訪問所有路由（因為可以切換身份模式）
    // 將系統帳號檢查提前，確保系統帳號可以訪問所有路由
    if (requiresAuth && accountType === 'system') {
        return next();
    }

    // 檢查帳號類型是否符合路由要求
    if (requiresAuth && requiredAccountType) {
        // 檢查帳號類型是否匹配
        let accountTypeMatches = false;

        // 如果 requiredAccountType 是陣列，檢查是否包含當前帳號類型
        if (Array.isArray(requiredAccountType)) {
            accountTypeMatches = requiredAccountType.includes(accountType);
        } else {
            accountTypeMatches = accountType === requiredAccountType;
        }

        if (!accountTypeMatches) {
            // 帳號類型不符，導向對應的 403 頁面（沒有權限）
            if (to.path.startsWith('/system')) {
                return next('/system/forbidden');
            } else if (to.path.startsWith('/platform')) {
                return next('/platform/forbidden');
            } else if (to.path.startsWith('/client')) {
                return next('/client/forbidden');
            }
            return next('/system/login');
        }

        if (requiredAccountRole) {
            const role = authService.getRole();
            const roleMatches = Array.isArray(requiredAccountRole)
                ? requiredAccountRole.includes(role)
                : role === requiredAccountRole;
            if (!roleMatches) {
                if (to.path.startsWith('/client')) {
                    return next('/client/forbidden');
                }
                return next('/system/forbidden');
            }
        }
    }

    return next();
});

export default router;
